import React from "react";
import { motion } from "framer-motion";
import {
  Code2,
  Smartphone,
  Sparkles,
  Cloud,
  Palette,
  Boxes,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const services = [
  {
    slug: "web",
    icon: Code2,
    title: "Web Development",
    desc: "Full-stack platforms with React, Next.js, Node, FastAPI. Pixel-perfect, performant, production-grade.",
    bullets: ["Frontend · React, Next.js, Vue", "Backend · Node, Python, Go", "APIs · REST, GraphQL, tRPC"],
    accent: "from-[#0A84FF]/40 to-transparent",
  },
  {
    slug: "mobile",
    icon: Smartphone,
    title: "App Development",
    desc: "iOS, Android, and cross-platform apps with native feel. Built with Swift, Kotlin, and React Native.",
    bullets: ["iOS · Swift / SwiftUI", "Android · Kotlin / Jetpack", "Cross-platform · React Native, Flutter"],
    accent: "from-[#F59E0B]/40 to-transparent",
  },
  {
    slug: "ai",
    icon: Sparkles,
    title: "AI & ML Solutions",
    desc: "Production AI — RAG, agents, vision, voice. Built on top of GPT, Claude, Gemini, and open-source models.",
    bullets: ["LLM Agents · OpenAI, Anthropic, Gemini", "Vector & RAG · Pinecone, Qdrant", "Vision & Voice · custom pipelines"],
    accent: "from-[#0A84FF]/40 to-transparent",
  },
  {
    slug: "cloud",
    icon: Cloud,
    title: "Cloud & DevOps",
    desc: "AWS, GCP, Azure infrastructure with IaC, CI/CD, observability. Built to scale to millions of users.",
    bullets: ["AWS · GCP · Azure", "Terraform · Kubernetes · Docker", "Observability · Datadog, Grafana"],
    accent: "from-white/10 to-transparent",
  },
  {
    slug: "design",
    icon: Palette,
    title: "UI / UX Design",
    desc: "Design systems and product interfaces engineered for delight. From research to shippable pixels.",
    bullets: ["Design systems · Figma → code", "Motion · Framer, Rive, Lottie", "Research · prototyping, testing"],
    accent: "from-[#F59E0B]/40 to-transparent",
  },
  {
    slug: "fullstack",
    icon: Boxes,
    title: "Full-stack Squads",
    desc: "Embedded senior squads — PM, design, engineering — that plug into your team and ship in weeks, not quarters.",
    bullets: ["Dedicated squads · 2–8 ppl", "Async · weekly demos", "Retainers · or fixed scope"],
    accent: "from-[#0A84FF]/40 to-transparent",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="relative py-20 md:py-40 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 md:gap-6 mb-10 md:mb-16">
          <div className="max-w-2xl">
            <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-[#0A84FF] mb-3 md:mb-4">
              What we do
            </p>
            <h2 className="font-display text-[10vw] sm:text-5xl lg:text-7xl font-black tracking-[-0.03em] leading-[1.02] text-grad">
              Six disciplines.<br />One studio.
            </h2>
          </div>
          <p className="text-[#a1a1a6] max-w-md text-sm sm:text-base md:text-lg leading-relaxed">
            From the first sketch to the millionth user, JDTECH owns the entire engineering lifecycle —
            so your team can focus on the product story.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                data-testid={SERVICES.card(s.slug)}
                className="glass relative overflow-hidden rounded-3xl p-6 md:p-8 group"
              >
                <div className={`absolute -top-24 -right-24 h-60 w-60 rounded-full blur-3xl opacity-50 bg-gradient-to-br ${s.accent}`} />
                <div className="relative">
                  <div className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 grid place-items-center mb-6 group-hover:bg-white/10 transition-colors">
                    <Icon size={20} strokeWidth={1.8} className="text-white" />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-3">
                    {s.title}
                  </h3>
                  <p className="text-[#a1a1a6] text-sm md:text-base leading-relaxed mb-6">
                    {s.desc}
                  </p>
                  <ul className="space-y-2 mb-8">
                    {s.bullets.map((b) => (
                      <li key={b} className="text-xs md:text-sm text-white/70 flex items-start gap-2">
                        <span className="mt-1.5 h-1 w-1 rounded-full bg-[#0A84FF] flex-none" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-2 text-sm font-medium text-white/80 group-hover:text-white transition-colors">
                    Talk to us
                    <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
